import type { Integration } from '@sentry/core';
import type { SentryNamespace } from '../base/interfaces/SentryNamespace';
import { BaseSentryAdapter } from '../base/BaseSentryAdapter';
import { SENTRY_CONFIG } from '../base/constants/SENTRY_CONFIG';
import { ENVIRONMENT_VALUES, ENVIRONMENTS } from '@shared/utils';
import type { AppError, AsyncResult, EnvironmentValue } from '@shared/utils';
import { tryCatchAsync } from '@shared/utils';
import { RUNTIME_KINDS } from '@shared/core';

/**
 * @group Logger Adapters
 * @summary
 * <blockquote>Browser-specific Sentry adapter — dynamically imports `@sentry/browser`,
 * configures tracing and replay integrations, and stores the singleton
 * Sentry namespace for all browser log calls.</blockquote>
 *
 * @example
 * ```typescript
 * import { BrowserSentryAdapter } from '@shared/logger/sentry/browser';
 *
 * const adapter = new BrowserSentryAdapter('warn');
 * await adapter.execute(logEntry);
 * ```
 */
export class BrowserSentryAdapter extends BaseSentryAdapter {
  readonly supportedRuntimes = [RUNTIME_KINDS.BROWSER] as const;

  private static sentryInstance: SentryNamespace | null = null;
  private static initLockPromise: AsyncResult<void, AppError> | null = null;

  protected getDsnEnvironmentKey(): EnvironmentValue {
    return ENVIRONMENT_VALUES.LOGGING.NEXT_PUBLIC_SENTRY_DSN;
  }

  static async initializeEarly(): AsyncResult<void, AppError> {
    const tempAdapter = new BrowserSentryAdapter('error');
    return tempAdapter.initializeSentry();
  }

  protected isSentryReady(): boolean {
    return BrowserSentryAdapter.sentryInstance !== null;
  }

  protected getInitPromise(): AsyncResult<void, AppError> | null {
    return BrowserSentryAdapter.initLockPromise;
  }

  protected setInitPromise(promise: AsyncResult<void, AppError> | null): void {
    BrowserSentryAdapter.initLockPromise = promise;
  }

  protected async performSentryInit(dsn: string, environment: string): AsyncResult<void, AppError> {
    return tryCatchAsync(async () => {
      const Sentry = await import('@sentry/browser');

      const integrations: Integration[] = [Sentry.browserTracingIntegration()];
      if (environment !== ENVIRONMENTS.DEVELOPMENT) {
        integrations.push(Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }));
      }

      Sentry.init({
        dsn,
        environment,
        tracesSampleRate: SENTRY_CONFIG.getTracesSampleRate(environment),
        integrations,
        debug: SENTRY_CONFIG.DEBUG,
        ...(environment !== ENVIRONMENTS.DEVELOPMENT && {
          replaysSessionSampleRate: SENTRY_CONFIG.DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
          replaysOnErrorSampleRate: SENTRY_CONFIG.DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
        }),
      });

      BrowserSentryAdapter.sentryInstance = Sentry as SentryNamespace;
    });
  }

  protected getSentryNamespace(): SentryNamespace | null {
    return BrowserSentryAdapter.sentryInstance;
  }

  protected getRuntimeTag(): string {
    return 'browser';
  }
}
